import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	Inject,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Source } from '@helpers/types/models';
import { exist } from '@campaign-portal/namespace/common/id';
import { SourcesService } from '../sources.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlarisDialogComponent } from '@campaign-portal/components-library';
import { TranslateModule } from '@ngx-translate/core';
import { MnpDeleteResponse } from '@helpers/types/api';

@Component({
	selector: 'app-sources-delete-dialog',
	templateUrl: './dialog.component.html',
	styleUrl: './dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [AlarisDialogComponent, TranslateModule],
})
export class SourcesDeleteDialogComponent implements OnInit {
	rulesSignal = signal<number[]>([]);

	private readonly destroyRef = inject(DestroyRef);

	constructor(
		@Inject(DIALOG_DATA) public readonly data: { source: Source<exist> },
		public readonly sourcesService: SourcesService,
		private readonly dialogRef: DialogRef<boolean>
	) {}

	ngOnInit(): void {
		switch (this.data.source.connectionType) {
			case 'CAMARA': {
				this.sourcesService
					.findSourceInCamaraRules({ sourceName: this.data.source.name })
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe((resp) => {
						this.rulesSignal.set(resp.rulesIds ?? []);
					});
				break;
			}
			case 'AI': {
				this.sourcesService
					.findSourceInAiRules({ sourceName: this.data.source.name })
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe((resp) => {
						this.rulesSignal.set(resp.rulesIds ?? []);
					});
				break;
			}
			default: {
				this.sourcesService
					.findSourceInRules({ choices: [this.data.source.name] })
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe((resp) => {
						this.rulesSignal.set(resp.rules_id_list ?? []);
					});
			}
		}
	}

	sourceUsed = computed(() => this.rulesSignal().length > 0);

	confirm(): void {
		if (this.sourceUsed()) {
			this.dialogRef.close(false);
			return;
		}
		this.sourcesService
			.delete({ id: this.data.source.sourceId! })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp: MnpDeleteResponse) => {
				this.dialogRef.close(resp.success);
			});
	}

	close(): void {
		this.dialogRef.close(false);
	}
}
